import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './AdminEditForm.css';
import privateAxios from '../../utils/axios/privateAxios';
import publicAxios from '../../utils/axios/publicAxios';
import Notification from '../../components/Notification/Notification';

const AdminEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [display, setDisplay] = useState(false); // ✅ Add display state
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification({ type: '', message: '' });
    }, 5000);
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const res = await privateAxios.get(`/private/admin-form/${id}`);
        const { title, description, imageUrl, display } = res.data.form;

        setTitle(title);
        setDescription(description);
        setImageUrl(imageUrl);
        setOriginalImageUrl(imageUrl);
        setDisplay(display); // ✅ Load display value
      } catch (err) {
        console.error('Failed to load form:', err);
        showNotification('error', 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) return originalImageUrl;

    try {
      const res = await privateAxios.get('/private/generate-upload-url', {
        params: {
          filename: imageFile.name,
          filetype: imageFile.type,
        },
      });

      const { url } = res.data;
      await publicAxios.put(url, imageFile);
      return url.split('?')[0];
    } catch (error) {
      console.error('Image upload failed:', error);
      throw new Error('Failed to upload image.');
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);

    try {
      let updatedImageUrl = originalImageUrl;

      if (imageFile) {
        updatedImageUrl = await uploadImageToS3();
      }

      await privateAxios.patch(`/private/admin-edit-form/${id}`, {
        title,
        description,
        imageUrl: updatedImageUrl,
        display, // ✅ Send display value
      });

      showNotification('success', 'Form updated successfully!');
      setTimeout(() => {
        navigate('/admin-forms');
      }, 3000);
    } catch (error) {
      console.error('Failed to update form:', error);
      showNotification('error', 'Failed to update form');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="admin-edit-form">
      <Notification
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ type: '', message: '' })}
      />

      <h2>Edit Form</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="edit-form">
          <label>Title:</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />

          <label>Change Image (optional):</label>
          <input type="file" accept="image/*" onChange={handleImageChange} />
          {imageUrl && (
            <div className="image-preview">
              <img src={imageUrl} alt="Preview" />
            </div>
          )}

          <label>Description:</label>
          <ReactQuill value={description} onChange={setDescription} theme="snow" className="quill-editor" />

          <div className="checkbox-display">
            <label>
              <input
                type="checkbox"
                checked={display}
                onChange={(e) => setDisplay(e.target.checked)}
              />
              Display
            </label>
          </div>

          <div className="edit-actions">
            <button onClick={handleUpdate} disabled={updating}>
              {updating ? 'Updating...' : 'Save Changes'}
            </button>
            <button onClick={() => navigate('/admin-forms')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditForm;